import AwardBadges from "@components/quiz/AwardBadges"
import FunnelLayout from "@components/quiz/FunnelLayout"
import QuizHeader from "@components/quiz/QuizHeader"
import StickyFooter from "@components/quiz/StickyFooter"
import {
  getSignUpEmailFromSessionStorage,
  setSignUpEmailInSessionStorage,
} from "@helpers/sessionStorage"
import { validateEmail } from "@helpers/validations"
import { LockClosedIcon } from "@heroicons/react/24/outline"
import { FormEvent, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"
import getPersonalization, { toInlineLabel } from "./personalization"
import { ONBOARDING_BASE_PATH, SignUpEmailState } from "./quizSteps"

export default function EmailPage() {
  const [email, setEmail] = useState(getSignUpEmailFromSessionStorage() ?? "")
  const [isInvalid, setIsInvalid] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const personalization = useMemo(() => getPersonalization(t), [t])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateEmail(email)) {
      setIsInvalid(true)
      return
    }

    setSignUpEmailInSessionStorage(email)
    // NOTE: Also in the history entry, which a reload keeps even when the
    // browser blocks storage (then sessionStorage is memory only).
    navigate(`${ONBOARDING_BASE_PATH}/password${location.search}`, {
      state: { email } satisfies SignUpEmailState,
    })
  }

  return (
    <FunnelLayout
      header={
        <QuizHeader
          onBack={() => navigate(-1)}
          section="plan"
          sectionProgress={1}
        />
      }
    >
      <form
        className="flex grow animate-slide-in-right flex-col gap-6 pt-4"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="animate-fade-up space-y-3">
          <h1 className="vm-heading">
            <Trans
              components={{ colored: <span className="vm-highlight" /> }}
              i18nKey="pages.onboarding.email.heading"
              values={{ goal: toInlineLabel(personalization.goalLabel) }}
            />
          </h1>
        </div>

        <div className="animate-fade-up space-y-3 [animation-delay:120ms]">
          <input
            autoComplete="email"
            autoFocus
            className="vm-input"
            data-invalid={isInvalid || undefined}
            inputMode="email"
            onChange={(event) => {
              setEmail(event.target.value)
              setIsInvalid(false)
            }}
            placeholder={t("pages.onboarding.email.placeholder")}
            type="email"
            value={email}
          />

          {isInvalid && (
            <p className="animate-fade-in text-sm font-medium text-vm-red">
              {t("pages.signUp.email.emailNotValid")}
            </p>
          )}

          <div className="flex gap-2 rounded-vm-md bg-vm-surface p-3 text-sm text-vm-muted">
            <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0 text-vm-muted" />
            <p>{t("pages.onboarding.email.privacy")}</p>
          </div>
        </div>

        <AwardBadges className="animate-fade-up [animation-delay:240ms]" />

        <StickyFooter>
          <div className="space-y-4">
            <button
              className="vm-button-primary"
              disabled={email.length === 0}
              type="submit"
            >
              {t("shared.continue")}
            </button>

            <p className="text-center text-xs leading-relaxed text-vm-muted">
              <Trans
                components={{
                  privacyPolicyLink: (
                    <Link
                      className="underline"
                      target="_blank"
                      to="https://www.visualmind.app/privacy.html"
                    />
                  ),
                  termsAndConditionsLink: (
                    <Link
                      className="underline"
                      target="_blank"
                      to="https://www.visualmind.app/terms.html"
                    />
                  ),
                }}
                i18nKey="pages.signUp.email.text"
              />
            </p>
          </div>
        </StickyFooter>
      </form>
    </FunnelLayout>
  )
}
